import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import MigrationSection, {
	type DryRunRecord,
	type MigrationRecord,
	useMigrationMatching,
} from './MigrationSection';

export default function JobTitlesSection() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const jobTitlesQuery = graphql.useMigrationJobTitlesQuery({
		variables: { isArchived: false, first: 1000 },
	});
	const dryRunQuery = graphql.useConnectorDryRunJobTitlesQuery({
		variables: { first: 1000 },
	});
	const [updateJobTitle] = graphql.useUpdateJobTitleMutation();

	const existing = useMemo(
		(): MigrationRecord[] =>
			[...(jobTitlesQuery.data?.jobTitles.edges ?? [])]
				.sort((a, b) => localize(a.node.title).text.localeCompare(localize(b.node.title).text))
				.map(({ node }) => ({
					id: node.id,
					externalReferenceID: node.externalReferenceID,
					matchKey: localize(node.title).text,
					name: localize(node.title).text,
				})),
		[jobTitlesQuery.data, localize],
	);

	const dryRun = useMemo(
		(): DryRunRecord[] =>
			[...(dryRunQuery.data?.viewer.workspace.connector?.dryRun.jobTitles.edges ?? [])]
				.sort((a, b) => a.node.title.localeCompare(b.node.title))
				.map(({ node, cursor }) => ({
					key: cursor,
					externalReferenceID: node.externalReferenceID,
					matchKey: node.title,
					name: node.title,
				})),
		[dryRunQuery.data],
	);

	const { matched, unmatched, newItems } = useMigrationMatching(existing, dryRun);

	const titleMap = useMemo(
		() => new Map((jobTitlesQuery.data?.jobTitles.edges ?? []).map(({ node }) => [node.id, node])),
		[jobTitlesQuery.data],
	);

	const handleSave = async (id: string, externalReferenceID: string) => {
		const jobTitle = titleMap.get(id);
		if (!jobTitle) throw new Error('Job title not found');
		await updateJobTitle({
			variables: {
				input: {
					id: jobTitle.id,
					title: jobTitle.title,
					externalReferenceID,
				},
			},
		});
	};

	return (
		<MigrationSection
			title={t('connectors.settings.migration.jobTitles.title')}
			loading={jobTitlesQuery.loading || dryRunQuery.loading}
			matched={matched}
			newItems={newItems}
			unmatched={unmatched}
			onSave={handleSave}
			onRefetch={() => jobTitlesQuery.refetch()}
			emptyMessage={t('connectors.settings.migration.jobTitles.empty')}
		/>
	);
}
